import { alpha, Backdrop, Box, Grid, Modal, RadioGroup, styled } from '@mui/material';
import { RadioButton } from '@/components/RadioButton/RadioButton';

export const ImportButton = styled(Box, {
    shouldForwardProp: prop => prop !== 'sideBar',
})<{ sideBar: boolean; disabled?: boolean }>(({ theme, sideBar, disabled }) => {
    const disabledColor = theme.palette.mode === 'dark' ? theme.custom.c5 : theme.custom.c4;

    return [
        {
            userSelect: 'none',
            justifyContent: 'center',
            display: 'flex',
            cursor: disabled ? 'not-allowed' : 'pointer',
            color: disabled ? disabledColor : 'primary.light',
            alignItems: 'center',
            padding: '8px 7px',
            borderRadius: '8px',
            gap: '16px',
            width: sideBar ? 'auto' : '40px',
            [theme.breakpoints.down('lg')]: {
                width: sideBar ? 'auto' : '36px',
            },
            '& svg': {
                fill: disabled ? disabledColor : theme.palette.mode === 'dark' ? theme.custom.c1 : theme.custom.c22,
            },
            '& .MuiTypography-root': {
                color: disabled ? disabledColor : 'inherit',
            },
        },
        sideBar && {
            justifyContent: 'flex-start',
            ...(!disabled && {
                '&:hover': {
                    background:
                        theme.palette.mode === 'dark'
                            ? alpha(theme.palette.secondary.light, 0.04)
                            : alpha(theme.palette.secondary.main, 0.1),
                    '& svg': {
                        color: theme.palette.secondary.main,
                        fill: theme.palette.secondary.main,
                    },
                },
            }),
        },
    ];
});

export const ImportModal = styled(Modal)(() => ({
    zIndex: 1000,
    padding: '86px 40px',
    '.MuiModal-backdrop': {
        backdropFilter: 'blur(15px)',
    },
}));

export const ImportModalContent = styled(Box, {
    shouldForwardProp: prop => prop !== 'sideBar',
})<{ sideBar: boolean }>(({ theme, sideBar }) => ({
    outline: 'none',
    width: sideBar ? 'calc(100% - 230px)' : 'calc(100% - 60px)',
    [theme.breakpoints.up('lg')]: {
        width: sideBar ? 'calc(100% - 248px)' : 'calc(100% - 64px)',
    },
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
}));

export const ImportModalWrap = styled(Box)(() => ({
    outline: 'none',
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
}));

export const ImportFormWrap = styled(Box)(() => ({
    display: 'grid',
    gridTemplateColumns: '392px 1fr',
    alignItems: 'flex-start',
    alignContent: 'flex-start',
    gap: '10px',
    maxWidth: '1266px',
}));

export const ImportForm = styled('form')(({ theme }) => ({
    borderRadius: '16px',
    padding: '24px 16px',
    background: theme.palette.mode === 'dark' ? theme.custom.c37 : theme.custom.c12,
    position: 'sticky',
    top: 0,
}));

export const ImportFormActions = styled(Box)(() => ({
    display: 'grid',
    gap: '20px',
    whiteSpace: 'nowrap',
    marginTop: '16px',
    gridTemplateColumns: 'minmax(calc(50% - 10px), min-content) auto',
    button: {
        paddingLeft: '24px',
        paddingRight: '24px',
    },
}));

export const ImportRepeatBlock = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: theme.custom.c11,
}));

export const ImportCheckboxGroup = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '14px',
    borderBottom: `1px solid ${theme.custom.c34}`,
    paddingBottom: '16px',
    marginTop: '16px',
    marginBottom: '12px',
}));

export const ImportRadio = styled(RadioButton)(({ theme }) => ({
    '.MuiFormControlLabel-label': {
        color: theme.custom.c47,
        lineHeight: '1',
        textTransform: 'unset',
        fontSize: '1rem',
    },
    '.MuiRadio-root': {
        width: '18px',
        height: '18px',
        border: `1px solid ${theme.custom.c47}`,
        svg: {
            display: 'none',
        },
        '&.Mui-checked': {
            border: `4px solid ${theme.palette.secondary.main}`,
            '& + .MuiFormControlLabel-label': {
                color: theme.palette.secondary.main,
            },
        },
    },
}));

export const ImportEpisode = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '8px',
}));

export const ImportEpisodeGroup = styled(RadioGroup)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    alignItems: 'flex-start',
    paddingBottom: '16px',
    marginBottom: '12px',
    borderBottom: `1px solid ${theme.custom.c34}`,
}));

export const ImportInfo = styled(Box)(() => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '8px',
    marginBottom: '16px',
    marginTop: '12px',
}));

export const ImportInfoBody = styled(Box)(({ theme }) => ({
    background: '#F4F4F5',
    borderRadius: '16px',
    padding: '24px 32px',
    color: theme.custom.c42,
}));

export const ImportInfoHead = styled(Box)(({ theme: _theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px',
}));

export const ImportLoader = styled(Backdrop)(({ theme: _theme }) => ({
    position: 'fixed',
    display: 'flex',
    justifyContent: 'center',
    backdropFilter: 'blur(3px)',
    zIndex: 1000,
}));

export const ImportLoaderWrap = styled(Box, {
    shouldForwardProp: prop => prop !== 'sideBar',
})<{ sideBar: boolean }>(({ theme, sideBar }) => ({
    display: 'grid',
    justifyContent: 'center',
    justifyItems: 'center',
    alignContent: 'center',
    flexWrap: 'wrap',
    gap: '24px',
    zIndex: '11000',
    width: sideBar ? 'calc(100% - 230px)' : 'calc(100% - 60px)',
    [theme.breakpoints.up('lg')]: {
        width: sideBar ? 'calc(100% - 248px)' : 'calc(100% - 64px)',
    },
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
}));

export const ImportInfoWrap = styled(Box)(() => ({
    display: 'grid',
    alignItems: 'flex-start',
    '& > *': { gridRow: 1, gridColumn: 1 },
}));

export const ImportStatisticsFound = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: '10px',
    color: theme.custom.c47,
    '.MuiTypography-root:last-child': {
        fontWeight: 600,
        color: theme.palette.mode === 'dark' ? theme.custom.c7 : theme.custom.c16,
    },
}));

export const ImportExceptionsHead = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '1px',
    minHeight: '42px',
    '.MuiTypography-root': {
        background: '#E4E8EF',
        fontWeight: 500,
        color: theme.custom.c36,
        textAlign: 'center',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
}));

export const ImportExceptionsRow = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: '1px',
    borderBottom: `1px solid ${theme.palette.mode === 'dark' ? theme.custom.c2 : theme.custom.c4}`,
}));

export const ImportSuccess = styled(Box)(({ theme: _theme }) => ({
    maxWidth: '320px',
    display: 'grid',
    gap: '24px',
}));

export const DroppableCharacters = styled(Grid)(({ theme: _theme }) => ({
    padding: '16px',
    minHeight: '108px',
    overflow: 'hidden',
    borderRadius: '12px',
    border: '2px dashed #D4D4D8',
}));
